import { Icon } from '@iconify/react';
import { useCallback, useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import searchFill from '@iconify/icons-eva/search-fill';
import { useDispatch } from 'react-redux';
import roundFilterList from '@iconify/icons-ic/round-filter-list';
import { MdUpdate } from 'react-icons/md';
import add from '@iconify/icons-ic/add';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import { BiCloudDownload } from 'react-icons/bi';
import { DateRangeStyled, RootStyle, SearchStyle } from '../Global/Styling';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { fDate } from '../../utils/formatTime';
import {
  getCityData,
  getLoanTypeData,
  getSourceListData,
  getStatusList
} from '../../store/Slice/Global/GlobalSlice';
import useLoginSelector from '../../store/Slice/Login/LoginSelector';
import useGlobalSelector from '../../store/Slice/Global/GlobalSelector';

export default function BankopsToolbar({
  filterName,
  onFilterName,
  filterDatesRange,
  setFilterDatesRange,
  filterState,
  setFilterState,
  parent,
  selected,
  setFilterName,
  handleDownload
}) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [isOpenFilter, openFilter] = useState(false);
  const { LoginData } = useLoginSelector();
  const { CityDrop, LoanTypeDrop, StatusDrop, SourceDrop } = useGlobalSelector();

  const handleRemoveFilter = useCallback(() => {
    setFilterDatesRange([{ startDate: null, endDate: null, key: 'selection' }]);
    setFilterState({
      product: ' ',
      productId: null,
      city: null,
      cityId: null,
      status: ' ',
      statusId: null,
      employmentType: ' '
    });
    setFilterName('');
  }, [setFilterDatesRange, setFilterState, setFilterName]);

  useEffect(() => {
    const commonObj = { companyId: LoginData?.companyId, userId: LoginData?.userId };
    dispatch(getLoanTypeData(commonObj));
    dispatch(getCityData({ ...commonObj, searchLiteral: '', sortOrder: 'desc' }));
    dispatch(getStatusList(58));
    dispatch(getSourceListData());
  }, [dispatch, LoginData]);

  const updateFilter = useCallback(
    (key, value, extra = {}) => {
      setFilterState((prev) => ({ ...prev, [key]: value, ...extra }));
    },
    [setFilterState]
  );

  return (
    <>
      {isOpenFilter && (
        <Box px={3} pt={2}>
          <Grid container spacing={3}>
            <Grid item xs>
              <Select
                fullWidth
                value={filterState.product}
                onChange={(e) => {
                  const val = e.target.value;
                  const parsed = val !== ' ' ? JSON.parse(val) : {};
                  updateFilter('product', val, { productId: parsed.loanId });
                }}
                inputProps={{
                  className: `textInput customSelectBox dissabledMenu${filterState.product}`
                }}
              >
                <MenuItem value=" " disabled>
                  Select Product
                </MenuItem>
                {LoanTypeDrop?.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs sx={{ position: 'relative' }}>
              <Box className="displayDateRange">
                {filterDatesRange[0].startDate ? `${fDate(filterDatesRange[0].startDate)} to ` : ''}
                {filterDatesRange[0].endDate ? fDate(filterDatesRange[0].endDate) : ''}
                {!filterDatesRange[0].startDate && !filterDatesRange[0].endDate && (
                  <Typography className="dissabledMenu">Select Date Range</Typography>
                )}
              </Box>
              <Select
                fullWidth
                inputProps={{ className: 'textInput' }}
                MenuProps={{ className: 'menuOpened' }}
                value=" "
                className="datesSelect"
              >
                <DateRangeStyled
                  editableDateInputs={false}
                  onChange={(item) => setFilterDatesRange([item.selection])}
                  moveRangeOnFirstSelection={false}
                  ranges={filterDatesRange}
                  className="dateRangePicker"
                />
              </Select>
            </Grid>
            <Grid item xs>
              <Autocomplete
                size="small"
                options={CityDrop || []}
                getOptionLabel={(option) => option.label || ''}
                value={filterState.city || null}
                renderInput={(params) => (
                  <TextField {...params} fullWidth placeholder="Select City" />
                )}
                onChange={(_, newValue) =>
                  updateFilter('city', newValue, { cityId: newValue?.value || null })
                }
              />
            </Grid>
            <Grid item xs>
              <Select
                fullWidth
                value={filterState.status}
                onChange={(e) => {
                  const val = e.target.value;
                  const parsed = val !== ' ' ? JSON.parse(val) : {};
                  updateFilter('status', val, { statusId: parsed.stageStatusId });
                }}
                inputProps={{
                  className: `textInput customSelectBox dissabledMenu${filterState.status}`
                }}
              >
                <MenuItem value=" " disabled>
                  Select Status
                </MenuItem>
                {StatusDrop?.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs sx={{ display: 'none' }}>
              <Select
                fullWidth
                value={filterState.employmentType}
                onChange={(e) => {
                  const val = e.target.value;
                  const parsed = val !== ' ' ? JSON.parse(val) : {};
                  updateFilter('employmentType', val, { employmentTypeId: parsed.typeId });
                }}
                inputProps={{
                  className: `textInput customSelectBox dissabledMenu${filterState.employmentType}`
                }}
              >
                <MenuItem value=" " disabled>
                  Select Source
                </MenuItem>
                {SourceDrop?.map((item) => (
                  <MenuItem key={item.value} value={item.value}>
                    {item.label}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            <Grid item xs="auto">
              <Tooltip title="Reset Filter">
                <IconButton sx={{ py: 0.8 }} onClick={handleRemoveFilter}>
                  <FilterListOffIcon />
                </IconButton>
              </Tooltip>
            </Grid>
          </Grid>
        </Box>
      )}

      <RootStyle sx={{ minHeight: 45, borderBottom: '1px solid #eaedf1' }}>
        <Box>
          <Tooltip title="Update" placement="top" arrow>
            <IconButton
              className={`squareIconButton ${selected?.length < 1 ? 'disabledDocument' : ''}`}
              onClick={() => {
                localStorage.setItem('selectedItem', JSON.stringify(selected));
                navigate(`/${parent}/update`);
              }}
            >
              <MdUpdate size={18} />
            </IconButton>
          </Tooltip>
          &nbsp;&nbsp;
          <Tooltip title="Add new Login Files" placement="top" arrow>
            <IconButton
              className="squareIconButton"
              onClick={() => navigate(`/${parent}/add-login-file`)}
            >
              <Icon icon={add} width={18} />
            </IconButton>
          </Tooltip>
          &nbsp;&nbsp;
          <Tooltip title="Download" placement="top" arrow>
            <IconButton className="squareIconButton" onClick={() => handleDownload(selected)}>
              <BiCloudDownload size={18} />
            </IconButton>
          </Tooltip>
        </Box>
        <Box>
          <SearchStyle
            value={filterName}
            onChange={onFilterName}
            placeholder="Search Application"
            classes={{ root: 'searchHolder', input: 'searchInput' }}
            startAdornment={
              <InputAdornment position="start">
                <Box component={Icon} icon={searchFill} sx={{ color: 'text.disabled' }} />
              </InputAdornment>
            }
          />
          &nbsp;&nbsp;&nbsp;
          <Tooltip title="Filter list">
            <IconButton onClick={() => openFilter(!isOpenFilter)}>
              <Icon icon={roundFilterList} />
            </IconButton>
          </Tooltip>
        </Box>
      </RootStyle>
    </>
  );
}
